import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { localeNames, locales, useI18n, type Locale } from '../i18n';

interface FlagProps {
  locale: Locale;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
}

function Star({ cx, cy, r, rot = 0, fill = '#ffde00' }: { cx: number; cy: number; r: number; rot?: number; fill?: string }) {
  const points: string[] = [];
  for (let i = 0; i < 10; i++) {
    const angle = (i * Math.PI) / 5 - Math.PI / 2;
    const radius = i % 2 === 0 ? r : r * 0.382;
    points.push(`${(cx + radius * Math.cos(angle)).toFixed(2)},${(cy + radius * Math.sin(angle)).toFixed(2)}`);
  }
  return <polygon points={points.join(' ')} fill={fill} transform={`rotate(${rot} ${cx} ${cy})`} />;
}

export function FlagIcon({ locale, width = 20, height = 14, style }: FlagProps) {
  const containerStyle: React.CSSProperties = {
    width: `${width}px`,
    height: `${height}px`,
    borderRadius: '2.5px',
    overflow: 'hidden',
    boxShadow: '0 0 0 1px rgba(125, 125, 125, 0.25)',
    flexShrink: 0,
    display: 'inline-flex',
    verticalAlign: 'middle',
    ...style
  };

  switch (locale) {
    case 'id':
      // Indonesia: Merah Putih
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="20" fill="#dc2626" />
          <rect y="20" width="60" height="20" fill="#ffffff" />
        </svg>
      );

    case 'es':
      // Spain: national flag used as language marker for neutral Spanish
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#aa151b" />
          <rect y="10" width="60" height="20" fill="#f1bf00" />
          <rect x="17" y="16" width="3" height="8" rx="0.6" fill="#aa151b" />
          <rect x="14" y="18" width="2" height="6" fill="#aa151b" />
        </svg>
      );

    case 'pt-br':
      // Brazil: national flag used for Brazilian Portuguese
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#009b3a" />
          <path d="M30 5 L54 20 L30 35 L6 20 Z" fill="#ffdf00" />
          <circle cx="30" cy="20" r="9" fill="#002776" />
          <path d="M21.8 18.3 C27 16.2 34.6 17.5 38.1 21.2" fill="none" stroke="#fff" strokeWidth="1.5" />
        </svg>
      );

    case 'ru':
      // Russia: national tricolor used as language marker
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="13.34" fill="#ffffff" />
          <rect y="13.33" width="60" height="13.34" fill="#0039a6" />
          <rect y="26.66" width="60" height="13.34" fill="#d52b1e" />
        </svg>
      );

    case 'tr':
      // Türkiye: national flag used as Turkish language marker
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#e30a17" />
          <circle cx="25" cy="20" r="11" fill="#ffffff" />
          <circle cx="28" cy="20" r="8.8" fill="#e30a17" />
          <Star cx={38} cy={20} r={5.3} rot={90} fill="#ffffff" />
        </svg>
      );

    case 'vi':
      // Vietnam: red field with central yellow star
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#da251d" />
          <Star cx={30} cy={20} r={11} fill="#ffcd00" />
        </svg>
      );

    case 'th':
      // Thailand: horizontal red, white, blue, white, and red bands
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#a51931" />
          <rect y="6.67" width="60" height="26.66" fill="#f4f5f8" />
          <rect y="13.33" width="60" height="13.34" fill="#2d2a4a" />
        </svg>
      );

    case 'ar':
      // Saudi Arabia: selector marker for neutral Modern Standard Arabic
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#006c35" />
          <g fill="#ffffff">
            <rect x="16" y="12" width="28" height="1.5" rx="0.75" />
            <rect x="19" y="15" width="22" height="1.5" rx="0.75" />
            <rect x="17" y="18" width="26" height="1.5" rx="0.75" />
            <path d="M14 27h31l-4 2H20l-3 2h-5l4-3z" />
          </g>
        </svg>
      );

    case 'en':
      // Great Britain: Union Jack
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#012169" />
          <path d="M0,0 L60,40 M60,0 L0,40" stroke="#ffffff" strokeWidth="8" />
          <path d="M0,0 L60,40 M60,0 L0,40" stroke="#c8102e" strokeWidth="4" />
          <path d="M30,0 V40 M0,20 H60" stroke="#ffffff" strokeWidth="12" />
          <path d="M30,0 V40 M0,20 H60" stroke="#c8102e" strokeWidth="7" />
        </svg>
      );

    case 'zh-cn':
      // China: Five-star Red Flag
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#de2910" />
          <Star cx={12} cy={13} r={7} />
          <Star cx={23} cy={6} r={2.4} rot={-20} />
          <Star cx={27} cy={11} r={2.4} rot={12} />
          <Star cx={27} cy={17} r={2.4} rot={38} />
          <Star cx={23} cy={22} r={2.4} rot={60} />
        </svg>
      );

    case 'ja':
      // Japan: Hinomaru
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#ffffff" />
          <circle cx="30" cy="20" r="11" fill="#bc002d" />
        </svg>
      );

    case 'ko':
      // South Korea: Taegeukgi
      return (
        <svg viewBox="0 0 60 40" style={containerStyle} aria-hidden="true">
          <rect width="60" height="40" fill="#ffffff" />
          {/* Taegeuk Circle — official diagonal orientation */}
          <g transform="rotate(-33 30 20)">
            <path d="M 18,20 A 12,12 0 0,1 42,20 A 6,6 0 0,1 30,20 A 6,6 0 0,0 18,20 Z" fill="#cd2e3a" />
            <path d="M 18,20 A 12,12 0 0,0 42,20 A 6,6 0 0,0 30,20 A 6,6 0 0,1 18,20 Z" fill="#0047a0" />
          </g>
          {/* 4 Trigrams */}
          {/* Top-left: Geon */}
          <g transform="translate(11, 8) rotate(33)">
            <rect x="-5" y="-3.2" width="10" height="1.3" fill="#111" />
            <rect x="-5" y="-0.9" width="10" height="1.3" fill="#111" />
            <rect x="-5" y="1.4" width="10" height="1.3" fill="#111" />
          </g>
          {/* Bottom-right: Gon */}
          <g transform="translate(49, 32) rotate(33)">
            <rect x="-5" y="-3.2" width="4.3" height="1.3" fill="#111" /><rect x="0.7" y="-3.2" width="4.3" height="1.3" fill="#111" />
            <rect x="-5" y="-0.9" width="4.3" height="1.3" fill="#111" /><rect x="0.7" y="-0.9" width="4.3" height="1.3" fill="#111" />
            <rect x="-5" y="1.4" width="4.3" height="1.3" fill="#111" /><rect x="0.7" y="1.4" width="4.3" height="1.3" fill="#111" />
          </g>
          {/* Top-right: Gam */}
          <g transform="translate(49, 8) rotate(-33)">
            <rect x="-5" y="-3.2" width="4.3" height="1.3" fill="#111" /><rect x="0.7" y="-3.2" width="4.3" height="1.3" fill="#111" />
            <rect x="-5" y="-0.9" width="10" height="1.3" fill="#111" />
            <rect x="-5" y="1.4" width="4.3" height="1.3" fill="#111" /><rect x="0.7" y="1.4" width="4.3" height="1.3" fill="#111" />
          </g>
          {/* Bottom-left: Ri */}
          <g transform="translate(11, 32) rotate(-33)">
            <rect x="-5" y="-3.2" width="10" height="1.3" fill="#111" />
            <rect x="-5" y="-0.9" width="4.3" height="1.3" fill="#111" /><rect x="0.7" y="-0.9" width="4.3" height="1.3" fill="#111" />
            <rect x="-5" y="1.4" width="10" height="1.3" fill="#111" />
          </g>
        </svg>
      );
  }
}

export default function LanguageSelector() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (next: Locale) => {
    setLocale(next);
    setOpen(false);
  };

  const shortCode = locale === 'zh-cn' ? 'ZH' : locale.toUpperCase();

  return (
    <div className="language-selector" ref={root}>
      <button
        id="language-selector-trigger"
        type="button"
        className="language-trigger"
        aria-label={`${t('nav.language')}: ${shortCode}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <FlagIcon locale={locale} width={18} height={12} />
        <span>{shortCode}</span>
        <ChevronDown size={13} className={open ? 'is-open' : ''} />
      </button>

      {open && (
        <div className="language-menu" role="listbox" aria-label={t('nav.language')}>
          {locales.map((item) => (
            <button
              type="button"
              role="option"
              aria-selected={locale === item}
              className={locale === item ? 'active' : ''}
              key={item}
              onClick={() => choose(item)}
            >
              <FlagIcon locale={item} width={20} height={14} />
              <span className="language-name">{localeNames[item]}</span>
              {locale === item && <Check size={14} style={{ marginLeft: 'auto' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
